import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ModeFilter, AdminMode } from '@/components/ui/ModeFilter';
import { TestBadge } from '@/components/ui/TestBadge';
import { Card, CardContent } from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Skeleton } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import { Tabs, TabsList, TabsTrigger } from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import {
  Truck,
  Package,
  PackageCheck,
  CheckCircle,
  Clock,
  MapPin,
  User,
  StickyNote,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api-helpers';
import { api } from '@/lib/api';

export const Route = createFileRoute('/_admin/envios')({
  component: EnviosPage,
});

interface ShipmentPrize {
  id: string;
  user_id: string;
  prize_name: string;
  prize_value: number;
  status: string;
  shipping_address: Record<string, string> | null;
  notes: string | null;
  tracking_code: string | null;
  created_at: string;
  updated_at: string;
  is_test?: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; icon: typeof Clock; color: string }> = {
  claimed: { label: 'Resgate solicitado', icon: PackageCheck, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  shipped: { label: 'Enviado', icon: Truck, color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  delivered: { label: 'Entregue', icon: CheckCircle, color: 'bg-green-500/20 text-green-400 border-green-500/30' },
};

type FilterStatus = 'all' | 'claimed' | 'shipped' | 'delivered';

function formatCurrency(value: number): string {
  // Backend amounts ja em reais (Decimal). Nao dividir por 100.
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatAddress(addr: Record<string, string> | null): string {
  if (!addr) return 'Endereco nao informado';
  const parts = [
    addr.street,
    addr.number && `no ${addr.number}`,
    addr.complement,
    addr.neighborhood,
    addr.city && addr.state && `${addr.city}/${addr.state}`,
    addr.cep && `CEP: ${addr.cep}`,
  ].filter(Boolean);
  return parts.join(', ');
}

function EnviosPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [mode, setMode] = useState<AdminMode>('all');
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    prize: ShipmentPrize | null;
    newStatus: string;
  }>({ open: false, prize: null, newStatus: '' });
  const [notesMap, setNotesMap] = useState<Record<string, string>>({});
  const [trackingMap, setTrackingMap] = useState<Record<string, string>>({});

  // W3: backend retorna { items, total, page, limit } — desempacotar items
  const { data: prizes, isPending } = useQuery({
    queryKey: ['admin-shipments', filter, mode],
    queryFn: () =>
      api
        .get<{ items: ShipmentPrize[]; total: number; page: number; limit: number }>(
          '/admin/financial/shipments',
          { params: { status: filter === 'all' ? undefined : filter, mode: mode !== 'all' ? mode : undefined } },
        )
        .then((r) => {
          const payload = r.data;
          // Suporta tanto array direto (legado) quanto { items } (W3)
          if (Array.isArray(payload)) return payload as ShipmentPrize[];
          return payload?.items ?? [];
        }),
    refetchInterval: 30000,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({
      id,
      newStatus,
      notes,
      trackingCode,
    }: {
      id: string;
      newStatus: string;
      notes?: string;
      trackingCode?: string;
    }) => {
      const body: Record<string, string | undefined> = { status: newStatus };
      if (notes) body.notes = notes;
      if (trackingCode) body.tracking_code = trackingCode.replace(/\s/g, '');
      return api.patch(`/admin/financial/shipments/${id}`, body);
    },
    onSuccess: () => {
      toast.success('Status atualizado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['admin-shipments'] });
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Erro ao atualizar status'));
    },
  });

  const handleStatusChange = () => {
    if (!actionDialog.prize) return;
    const notes = notesMap[actionDialog.prize.id];
    const trackingCode = trackingMap[actionDialog.prize.id]?.replace(/\s/g, '');

    if (actionDialog.newStatus === 'shipped' && !trackingCode?.trim()) {
      toast.error('Informe o código de rastreio para marcar como enviado.');
      return;
    }

    updateStatusMutation.mutate({
      id: actionDialog.prize.id,
      newStatus: actionDialog.newStatus,
      notes: notes || undefined,
      trackingCode: trackingCode?.trim() || undefined,
    });
    setActionDialog({ open: false, prize: null, newStatus: '' });
  };

  const getStatusLabel = (newStatus: string) => {
    if (newStatus === 'shipped') return 'Marcar como Enviado';
    if (newStatus === 'delivered') return 'Marcar como Entregue';
    return 'Atualizar';
  };

  if (isPending) {
    return (
      <div className="space-y-4">
        <div>
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Truck className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Envios</h1>
            <p className="text-muted-foreground">Gerencie os envios de prêmios físicos</p>
          </div>
        </div>
        <ModeFilter value={mode} onChange={setMode} />
      </div>

      <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterStatus)}>
        <TabsList>
          <TabsTrigger value="all">Todos</TabsTrigger>
          <TabsTrigger value="claimed">Pendentes</TabsTrigger>
          <TabsTrigger value="shipped">Enviados</TabsTrigger>
          <TabsTrigger value="delivered">Entregues</TabsTrigger>
        </TabsList>
      </Tabs>

      {prizes && prizes.length > 0 ? (
        <div className="grid gap-4">
          {prizes.map((prize) => {
            const status = STATUS_CONFIG[prize.status] || STATUS_CONFIG.claimed;
            const StatusIcon = status.icon;

            return (
              <Card key={prize.id}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Package className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold text-lg">{prize.prize_name}</h3>
                        <Badge variant="outline" className={status.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {status.label}
                        </Badge>
                        <TestBadge isTest={!!prize.is_test} size="xs" />
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <span>Valor: {formatCurrency(prize.prize_value)}</span>
                        <span>•</span>
                        <span>{format(new Date(prize.updated_at), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="h-4 w-4" />
                        <span className="font-mono text-xs">{prize.user_id.slice(0, 8)}...</span>
                      </div>

                      <div className="flex items-start gap-2 text-sm bg-muted/50 rounded-lg p-3">
                        <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                        <span>{formatAddress(prize.shipping_address)}</span>
                      </div>

                      {prize.tracking_code && (
                        <div className="flex items-center gap-2 text-sm">
                          <Truck className="h-4 w-4 shrink-0 text-purple-400" />
                          <a
                            href={`https://seurastreio.com.br/rastreio/${prize.tracking_code}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs text-primary hover:underline"
                          >
                            {prize.tracking_code}
                          </a>
                        </div>
                      )}

                      {prize.notes && (
                        <div className="flex items-start gap-2 text-sm text-muted-foreground">
                          <StickyNote className="h-4 w-4 mt-0.5 shrink-0" />
                          <span>{prize.notes}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 shrink-0">
                      {prize.status === 'claimed' && (
                        <Button
                          size="sm"
                          onClick={() => setActionDialog({ open: true, prize, newStatus: 'shipped' })}
                          disabled={updateStatusMutation.isPending}
                        >
                          <Truck className="h-4 w-4 mr-1" />
                          Enviado
                        </Button>
                      )}
                      {prize.status === 'shipped' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setActionDialog({ open: true, prize, newStatus: 'delivered' })}
                          disabled={updateStatusMutation.isPending}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Entregue
                        </Button>
                      )}
                    </div>
                  </div>

                  {prize.status === 'claimed' && (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        placeholder="Código de rastreio (ex: AA123456789BR)"
                        value={trackingMap[prize.id] || ''}
                        onChange={(e) =>
                          setTrackingMap((prev) => ({ ...prev, [prize.id]: e.target.value }))
                        }
                        className="text-sm font-mono flex-1"
                      />
                      <Input
                        placeholder="Observações (opcional)"
                        value={notesMap[prize.id] || ''}
                        onChange={(e) =>
                          setNotesMap((prev) => ({ ...prev, [prize.id]: e.target.value }))
                        }
                        className="text-sm flex-1"
                      />
                    </div>
                  )}
                  {prize.status === 'shipped' && (
                    <Input
                      placeholder="Observações"
                      value={notesMap[prize.id] || ''}
                      onChange={(e) =>
                        setNotesMap((prev) => ({ ...prev, [prize.id]: e.target.value }))
                      }
                      className="text-sm"
                    />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Nenhum envio</h3>
          <p className="text-muted-foreground">Nao ha envios pendentes no momento.</p>
        </div>
      )}

      <ConfirmModal
        isOpen={actionDialog.open}
        onClose={() => setActionDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={handleStatusChange}
        title={getStatusLabel(actionDialog.newStatus)}
        description={`Confirma a alteração do status do prêmio "${actionDialog.prize?.prize_name}" para ${STATUS_CONFIG[actionDialog.newStatus]?.label || actionDialog.newStatus}?`}
        confirmText="Confirmar"
        cancelText="Cancelar"
        isLoading={updateStatusMutation.isPending}
      />
    </div>
  );
}
